/**
 * "Üres" induló dataset — élesszerű kezdés a böngészőben.
 * Csak a tenant_id (egyetlen) van rögzítve a multi-tenant scaffold-hoz.
 * Az első user (admin) és az első cég a Setup wizard során jön létre.
 *
 * Amikor a NestJS backend megérkezik, ez a fájl feleslegessé válik
 * (a backend kezeli a tenant init-et és az első user létrehozást).
 */

import type {
  AccountantExport,
  Company,
  CompanyMembership,
  DiscrepancyIssue,
  EmailInboxItem,
  InvoiceListItem,
  JobRun,
  Notification,
  User,
  VatMonthlySummary,
} from './types'

const TENANT_ID = 't0000000-0000-0000-0000-000000000001'

/** Üres user térkép — Setup wizard tölti meg. */
const USERS: Record<string, User> = {}

/** Üres cég térkép — Setup wizard hozza az elsőt. */
const COMPANIES: Record<string, Company> = {}

/** Üres membership térkép. */
const MEMBERSHIPS: Record<string, CompanyMembership[]> = {}

export const INVOICES: InvoiceListItem[] = []
export const DISCREPANCIES: DiscrepancyIssue[] = []

/** Üres VAT összesítő — minden mező 0, no review. */
export const VAT_SUMMARY: VatMonthlySummary = {
  companyId: '',
  periodYear: new Date().getFullYear(),
  periodMonth: new Date().getMonth() + 1,
  status: 'draft',
  currency: 'HUF',
  totals: {
    vatPayable: 0,
    vatDeductibleMarked: 0,
    vatNonDeductibleMarked: 0,
    reverseChargeMarked: 0,
    euAcquisitionMarked: 0,
    thirdCountryServiceMarked: 0,
    exemptMarked: 0,
  },
  needsReview: [],
  generatedAt: new Date().toISOString(),
  disclaimer: 'Ez nem hivatalos ÁFA-bevallás. Könyvelői ellenőrzés szükséges.',
}

/** Üres export package — nincs még generálva. */
export const EXPORT_PACKAGE: AccountantExport = {
  id: '',
  companyId: '',
  periodYear: new Date().getFullYear(),
  periodMonth: new Date().getMonth() + 1,
  version: 1,
  supersededAt: null,
  supersededById: null,
  previousVersionId: null,
  status: 'draft',
  counts: { invoices: 0, missingPdf: 0, navOnly: 0, discrepancies: 0 },
  vatPrecalc: {
    vatPayable: 0,
    vatDeductibleMarked: 0,
    vatNonDeductibleMarked: 0,
    reverseChargeMarked: 0,
    euAcquisitionMarked: 0,
    thirdCountryServiceMarked: 0,
    exemptMarked: 0,
    needsReviewCount: 0,
    currency: 'HUF',
  },
  packageFile: null,
  executiveSummary: {},
  accountantNotes: null,
  approvalRequestedAt: null,
  approvedAt: null,
  sentAt: null,
  sentToEmail: null,
}

export const JOBS: JobRun[] = []
export const NOTIFICATIONS: Notification[] = []
export const INBOX: EmailInboxItem[] = []

export const FIXTURES = {
  TENANT_ID,
  COMPANY_A: '',
  COMPANY_B: '',
  USERS,
  COMPANIES,
  MEMBERSHIPS,
  INVOICES,
  DISCREPANCIES,
  VAT_SUMMARY,
  EXPORT_PACKAGE,
  JOBS,
  NOTIFICATIONS,
  INBOX,
}

/** Az invoice detail építő üres állapotra is működik. */
export function buildInvoiceDetail(item: InvoiceListItem) {
  return {
    ...item,
    tenantId: TENANT_ID,
    companyId: '',
    direction: 'incoming' as const,
    supplier: { name: item.supplierName, taxNumber: item.supplierTaxNumber, countryCode: null },
    buyer: { name: null, taxNumber: null, countryCode: 'HU' },
    amounts: {
      net: item.netAmount,
      vat: item.vatAmount,
      gross: item.grossAmount,
      currency: item.currency,
      exchangeRate: null,
    },
    vatCategory: null,
    vatRatePercent: null,
    files: {},
    matching: {
      status: item.matchingStatus,
      score: item.matchingScore,
      algorithmVersion: 'v1.0',
      matchedNavDocumentId: null,
      fieldScores: [],
      discrepancies: [],
    },
    workflow: {
      status: item.workflowStatus,
      history: [
        { fromStatus: null, toStatus: 'received' as const, at: new Date().toISOString(), actor: 'system' },
      ],
    },
    approval: { status: item.approvalStatus },
    payment: { status: item.paymentStatus, records: [] },
    notes: null,
    extractionConfidence: null,
    isModification: false,
    isCancellation: false,
    modifiesInvoiceId: null,
    costCenter: null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    archivedAt: null,
  }
}
